import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { Heading } from '@spark-web/heading';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQueryState } from 'nuqs';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';

import { CategoryIcon } from '~/components/common/category-icon';
import { AddIcon, CheckIcon } from '~/components/common/icon';
import { Tooltip } from '~/components/common/tooltip';
import { CHANNEL_TO_PARTNER, CHANNEL_VENDOR_INFO, PARTNER_WITH_PRICING_DISPLAY } from '~/constants';
import { AnalyticsEvents } from '~/constants/analytics';
import { usePartnerTheming } from '~/hooks/usePartnerTheming';
import { useAnalyticsContext } from '~/provider/AnalyticsProvider';
import { useLeadContext } from '~/provider/LeadProvider';
import { useVendorContext } from '~/provider/VendorProvider';
import { LDFeatureFlag } from '~/types/feature-flag';
import { MarketplaceChannel, MarketplacePartner, MarketplaceVendor } from '~/types/marketplace';
import { formatDollars } from '~/utils/formatter';

import { LocalVendor } from './local-vendor';
import { VendorCardPromoInfo } from './vendor-card-promo-info';
import { VendorDescription } from './vendor-description';
import { VendorHighlights } from './vendor-highlights';
import { VendorImage } from './vendor-image';
import { VendorRating } from './vendor-rating';

type FetcherData = {
  error?: string;
};

type VendorActionButtonIconProps = {
  isSelected: boolean;
  partner?: MarketplacePartner;
};

type VendorCardProps = {
  vendor: MarketplaceVendor;
  channel?: MarketplaceChannel | null;
  promoCode?: string;
  index: number;
};

const VendorActionButtonIcon: FC<VendorActionButtonIconProps> = ({ isSelected, partner }) => {
  return isSelected ? <CheckIcon partner={partner} /> : <AddIcon partner={partner} />;
};

export const VendorCard: FC<VendorCardProps> = ({ vendor, channel, promoCode, index }) => {
  const { marketplaceSorting } = useFlags<LDFeatureFlag>();
  const fetcher = useFetcher<FetcherData>();
  const { t } = useTranslation();

  const { lead } = useLeadContext();
  const {
    selectedVendors,
    vendorSelectionLimitReached,
    previouslySelectedVendorIds,
    setSelectedVendors,
    addPreviouslySelectedVendor,
  } = useVendorContext();
  const { track } = useAnalyticsContext();
  const {
    card: { borderRadius, css: cardCss },
    tooltip: { tone },
  } = usePartnerTheming();

  const partner = CHANNEL_TO_PARTNER(channel);

  const [categories] = useQueryState('categories');

  const vendorDescription = vendor.details.length > 0 ? vendor.details?.[0].description : null;

  const selectedCategories = lead ? lead?.categorySlugs : typeof categories === 'string' ? categories.split(',') : [];
  const displayCategories = vendor.categories?.filter(({ slug }) => selectedCategories.includes(slug));

  const totalPackage = useMemo(
    () => displayCategories?.reduce((acc, cat) => acc + (cat?.indicativePrice ?? 0), 0),
    [displayCategories],
  );
  const isSelected = useMemo(
    () => selectedVendors.find((selectedVendor) => selectedVendor.publicId === vendor.publicId),
    [selectedVendors, vendor],
  );

  const isPreviouslySelected = previouslySelectedVendorIds.includes(vendor.publicId);

  const registeredRef = useRef(false);
  useEffect(() => {
    if (isPreviouslySelected && !registeredRef.current) {
      registeredRef.current = true;
      addPreviouslySelectedVendor(vendor);
    }
  }, [isPreviouslySelected, addPreviouslySelectedVendor, vendor]);

  const handleVendorSelection = (selectedVendor: MarketplaceVendor) => {
    const displayPosition = index + 1; // 1-based index for readability
    if (!isSelected) {
      track(AnalyticsEvents.VendorSelected, {
        id: lead?.id ?? '',
        vendorName: selectedVendor.tradingName,
        positionIndex: displayPosition,
        marketplaceSorting,
      });
    }
    setSelectedVendors(
      isSelected
        ? selectedVendors.filter((vendor) => vendor.publicId !== selectedVendor.publicId)
        : [
            ...selectedVendors,
            {
              ...selectedVendor,
              sortPosition: displayPosition,
              sortMethod: marketplaceSorting,
            },
          ],
    );

    if (lead) {
      fetcher.submit(
        { leadId: lead.id, vendorId: selectedVendor.publicId },
        { method: 'post', action: '/api/marketplace/vendor-selected' },
      );
    }
  };

  const isPricingVisible = partner ? PARTNER_WITH_PRICING_DISPLAY.includes(partner) : false;

  // promo pill for vendor that provide the promo
  const vendorPromo = useMemo(() => {
    return vendor.marketplacePromotions?.find((promo) => promo.code === promoCode);
  }, [promoCode, vendor.marketplacePromotions]);

  return (
    <Box
      borderRadius={borderRadius}
      padding="large"
      background="surface"
      width="full"
      display="flex"
      flexDirection="column"
      justifyContent="spaceBetween"
      css={css(cardCss)}
    >
      <Stack gap="large">
        <Stack gap="medium">
          <VendorImage logoUrl={vendor.logoUrl} tradingName={vendor.tradingName} />
          <Heading level="3" id={vendor.publicId}>
            {vendor.tradingName}
          </Heading>
          {vendor.rating?.rating ? <VendorRating rating={vendor.rating} /> : <Box height="xxsmall" />}
        </Stack>
        {vendor.isLocal && (
          <Box display="flex" flexDirection="row" gap="small" alignItems="center">
            <LocalVendor partner={partner} />
            {vendor?.details?.[0]?.addressDetail?.suburb && (
              <Text size="small">Based in {vendor.details[0].addressDetail.suburb}</Text>
            )}
          </Box>
        )}

        {vendorPromo && <VendorCardPromoInfo promo={vendorPromo} />}

        {vendorDescription ? <VendorDescription description={vendorDescription} /> : null}
        <Divider />

        <VendorHighlights partner={partner} />

        <Divider />

        <Stack gap="large">
          {isPricingVisible && (
            <Tooltip title="Price guideline">
              <Stack gap="xlarge">
                <Text size="xsmall" tone={tone}>
                  The price guideline is indicative only for completion of the work.
                </Text>
                <Text size="xsmall" tone={tone}>
                  The pricing reflects considerations for government rebates, and covers all costs from quoting,
                  installation and purchase of equipment.
                </Text>
              </Stack>
            </Tooltip>
          )}

          <Stack gap="large" paddingBottom="medium">
            {displayCategories?.map((category) => (
              <Box
                key={category.slug}
                display="flex"
                flexDirection="row"
                justifyContent="spaceBetween"
                alignItems="center"
              >
                <Box display="flex" alignItems="center" flexDirection="row" gap="small">
                  <CategoryIcon slug={category.slug} name={category.name} partner={partner} />
                  <Text size="xsmall">{category.name}</Text>
                </Box>
                {isPricingVisible && category.indicativePrice ? (
                  <Text
                    size="xsmall"
                    css={css({
                      whiteSpace: 'nowrap',
                    })}
                  >
                    From {formatDollars(category.indicativePrice)}
                  </Text>
                ) : null}
              </Box>
            ))}
          </Stack>
        </Stack>
      </Stack>
      <Stack gap="xlarge">
        {isPricingVisible && totalPackage && totalPackage > 0 ? (
          <>
            <Divider />
            <Box display="flex" flexDirection="row" justifyContent="spaceBetween">
              <Text size="xsmall" weight="semibold">
                Total package from
              </Text>
              <Text size="xsmall">{formatDollars(totalPackage)}</Text>
            </Box>
          </>
        ) : null}

        {channel && channel in CHANNEL_VENDOR_INFO ? CHANNEL_VENDOR_INFO[channel]?.(t) : null}

        {isPreviouslySelected || isSelected ? (
          <Button
            rounded
            tone="primary"
            prominence="low"
            filled
            onClick={isPreviouslySelected ? undefined : () => handleVendorSelection(vendor)}
            css={css({ borderWidth: 1 })}
            disabled={isPreviouslySelected}
            aria-describedby={vendor.publicId}
          >
            <VendorActionButtonIcon isSelected partner={partner} />
            {isPreviouslySelected ? 'Already requested' : 'Added'}
          </Button>
        ) : (
          <Button
            rounded
            tone="primary"
            prominence="low"
            onClick={() => handleVendorSelection(vendor)}
            css={css({ borderWidth: 1 })}
            disabled={vendorSelectionLimitReached}
            aria-describedby={vendor.publicId}
          >
            <VendorActionButtonIcon isSelected={false} partner={partner} />
            Add to request
          </Button>
        )}
      </Stack>
    </Box>
  );
};
